import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DateRangeCalendar = ({
  startDate,
  endDate,
  onDateChange,
  minDate,
  maxDate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedStartDate, setSelectedStartDate] = useState(
    startDate || new Date()
  );
  const [selectedEndDate, setSelectedEndDate] = useState(endDate || new Date());
  const [selectionPhase, setSelectionPhase] = useState(null); // null, "start", "end"

  useEffect(() => {
    if (startDate) {
      setSelectedStartDate(startDate);
      setCurrentMonth(new Date(startDate));
    }
    if (endDate) {
      setSelectedEndDate(endDate);
    }
  }, [startDate, endDate]);

  const formatDate = (date) => {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      day: "2-digit",
      month: "short",
    }).format(date);
  };

  const formatMonthYear = (date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      year: "numeric",
    }).format(date);
  };

  const handleDayClick = (day) => {
    if (!selectionPhase || selectionPhase === "start") {
      setSelectedStartDate(day);
      setSelectedEndDate(day);
      setSelectionPhase("end");
    } else if (selectionPhase === "end") {
      if (day < selectedStartDate) {
        setSelectedStartDate(day);
        setSelectedEndDate(selectedStartDate);
      } else {
        setSelectedEndDate(day);
      }
      setSelectionPhase(null);
    }
  };

  const handlePrevMonth = () => {
    setCurrentMonth((prevMonth) => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(prevMonth.getMonth() - 1);
      return newMonth;
    });
  };

  const handleNextMonth = () => {
    setCurrentMonth((prevMonth) => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(prevMonth.getMonth() + 1);
      return newMonth;
    });
  };

  const generateCalendarDays = () => {
    const days = [];
    const firstDayOfMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      1
    );
    const lastDayOfMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      0
    );

    // Get the day of week for the first day (0 = Sunday, 6 = Saturday)
    const firstDayOfWeek = firstDayOfMonth.getDay();

    // Add days from previous month to fill the first week
    const prevMonthLastDay = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      0
    ).getDate();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const day = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() - 1,
        prevMonthLastDay - i
      );
      days.push({ date: day, isCurrentMonth: false });
    }

    // Add days of current month
    for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
      const day = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        i
      );
      days.push({ date: day, isCurrentMonth: true });
    }

    // Add days from next month to complete the grid
    const remainingDays = 42 - days.length; // 6 rows x 7 days
    for (let i = 1; i <= remainingDays; i++) {
      const day = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + 1,
        i
      );
      days.push({ date: day, isCurrentMonth: false });
    }

    return days;
  };

  const isDateInRange = (date) => {
    return date >= selectedStartDate && date <= selectedEndDate;
  };

  const isStartDate = (date) => {
    return date.toDateString() === selectedStartDate.toDateString();
  };

  const isEndDate = (date) => {
    return date.toDateString() === selectedEndDate.toDateString();
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const handleApply = () => {
    onDateChange(selectedStartDate, selectedEndDate);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
    setIsOpen(false);
  };

  const calendarDays = generateCalendarDays();

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(true)}
        className="px-4 py-2 text-sm bg-p400 border border-n200 rounded-md shadow-sm">
        Change Date
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-n100 rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            {/* Header with From/To dates */}
            <div className="grid grid-cols-2 divide-x divide-n200">
              <div className="p-4 text-center">
                <div className="text-n500 text-sm">From</div>
                <div className="font-medium">
                  {formatDate(selectedStartDate)}
                </div>
              </div>
              <div className="p-4 text-center">
                <div className="text-n500 text-sm">To</div>
                <div className="font-medium">{formatDate(selectedEndDate)}</div>
              </div>
            </div>

            {/* Calendar header */}
            <div className="flex items-center justify-between p-4 border-t border-b border-n200">
              <button
                onClick={handlePrevMonth}
                className="p-2 rounded-full hover:bg-n200">
                <ChevronLeft size={20} />
              </button>
              <div className="text-lg font-medium">
                {formatMonthYear(currentMonth)}
              </div>
              <button
                onClick={handleNextMonth}
                className="p-2 rounded-full hover:bg-n200">
                <ChevronRight size={20} />
              </button>
            </div>

            {/* Calendar grid */}
            <div className="p-2">
              {/* Weekday headers */}
              <div className="grid grid-cols-7 mb-1">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
                  (day) => (
                    <div
                      key={day}
                      className="text-center text-sm font-medium py-2">
                      {day}
                    </div>
                  )
                )}
              </div>

              {/* Calendar days */}
              <div className="grid grid-cols-7">
                {calendarDays.map((dayInfo, index) => {
                  const { date, isCurrentMonth } = dayInfo;
                  const inRange = isDateInRange(date);
                  const isStart = isStartDate(date);
                  const isEnd = isEndDate(date);

                  let dayClasses =
                    "w-[70%] h-10 flex items-center justify-center rounded-full mx-auto";

                  if (!isCurrentMonth) {
                    dayClasses += " text-gray-300";
                  } else if (isStart || isEnd) {
                    dayClasses += " bg-black text-white";
                  } else if (inRange) {
                    dayClasses += "";
                  } else if (isToday(date)) {
                    dayClasses += " border border-n300";
                  }

                  return (
                    <div
                      key={index}
                      className={`py-1 relative ${inRange ? "bg-n200 border-0" : ""}`}>
                      <button
                        className={dayClasses}
                        onClick={() => handleDayClick(date)}
                        disabled={!isCurrentMonth}>
                        {date.getDate()}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 flex space-x-2 border-t border-n200">
              <button
                onClick={handleCancel}
                className="w-full py-2 px-4 bg-p400 border border-n200 rounded-md text-p300 font-medium">
                Cancel
              </button>
              <button
                onClick={handleApply}
                className="w-full py-2 px-4 bg-swipe-button text-p400 rounded-md font-medium">
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeCalendar;
